import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTheme } from 'styled-components';
import type { MarketConfig } from '../../contexts';
import type { Market } from '../../types';
import { Button, IconButton } from '../Button';
import type { FormControlSizes } from '../Form/types';
import { Icon, IconName } from '../Icons';
import { Guide, GuideContent, GuideImage, GuideStep, GuideText, GuideWrapper, Index, Indexes } from './styles';

export const OnboardingGuide = ({
  guide,
  externalIP,
  formControlSize,
  selectedMarket,
}: {
  guide?: MarketConfig['guide'];
  externalIP: string;
  formControlSize: FormControlSizes;
  selectedMarket: Market;
}) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const guideElementRef = useRef<HTMLDivElement>(null);

  const replaceExternalIP = (step: string) => {
    return step.replaceAll('{{ExternalIP}}', externalIP);
  };

  useEffect(() => {
    const items = Array.from(guideElementRef.current?.querySelectorAll('[role="listitem"]') ?? []);
    const intersectionObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const intersectingIndex = items.indexOf(entry.target);
          if (entry.isIntersecting) {
            setCurrentStep(intersectingIndex);
            break;
          }
        }
      },
      // When switching between pages, when half out element is outside and other half is visible
      // this will trigger the event, and isIntersecting will be true for entering slide
      { root: guideElementRef.current, threshold: [0.5] }
    );
    items.forEach(item => intersectionObserver.observe(item));
    return () => {
      intersectionObserver.disconnect();
    };
  }, [theme, guide]);

  const setStep = (index: number) => {
    if (guideElementRef.current != null) {
      guideElementRef.current?.scrollTo({
        left: (guideElementRef.current?.offsetWidth ?? 0) * index,
        top: 0,
        behavior: 'smooth',
      });
    }
  };
  const prevStep = () => setStep(currentStep - 1);
  const nextStep = () => setStep(currentStep + 1);

  return (
    <GuideWrapper>
      {guide != null && guide.length > 1 && (
        <Indexes>
          <IconButton
            icon={IconName.ChevronLeft}
            size={formControlSize}
            disabled={currentStep === 0}
            onClick={prevStep}
          />
          <div>
            {guide?.map((_, index) => (
              <Index key={index} onClick={() => setStep(index)}>
                {index <= currentStep && (
                  <Icon
                    icon={IconName.DotSolid}
                    color={index === currentStep ? theme.colorTextPrimary : theme.colorTextMuted}
                  />
                )}
              </Index>
            ))}
          </div>
          <Button
            endIcon={IconName.ChevronRight}
            size={formControlSize}
            disabled={currentStep === guide.length - 1}
            onClick={nextStep}
          >
            Next
          </Button>
        </Indexes>
      )}
      {guide != null ? (
        <Guide ref={guideElementRef}>
          {guide.map((step, index) => (
            <GuideStep key={index} role="listitem">
              <GuideContent>
                <GuideText>
                  <h2 style={{ marginTop: 0 }}>{step.header}</h2>
                  <ReactMarkdown children={replaceExternalIP(step.text)} />
                </GuideText>
                <GuideImage>{step.image && <img src={step.image} alt="Screenshot" />}</GuideImage>
              </GuideContent>
            </GuideStep>
          ))}
        </Guide>
      ) : (
        <GuideContent>
          <GuideText>
            <h2>Contact Us</h2>
            <p>Contact support@talostrading.com for help getting onboarded to {selectedMarket.DisplayName}</p>
          </GuideText>
        </GuideContent>
      )}
    </GuideWrapper>
  );
};
